import { userActivity } from './user-activity'
import authLogger from './auth-logger'

const MIN_REFRESH_DELAY_MS = 10_000
const REFRESH_AT_FRACTION = 0.9

class TokenRefreshService {
  _refreshFn = null
  _onRefreshError = null
  _refreshTimer = null
  _refreshPromise = null

  /**
   * Configure the refresh service with callbacks.
   * Can be called multiple times to update callbacks (e.g. on React re-renders).
   * @param {Object} options
   * @param {() => Promise<{expiresIn?: number}>} options.refreshFn - returns fresh token info
   * @param {(error: Error) => void} [options.onRefreshError] - called when any refresh attempt fails
   */
  configure({ refreshFn, onRefreshError }) {
    this._refreshFn = refreshFn
    this._onRefreshError = onRefreshError ?? null
    authLogger.debug('token refresh service configured')
  }

  /**
   * Schedule a proactive token refresh at ~90% of the token TTL.
   * @param {number} expiresIn - seconds until token expires
   */
  scheduleRefresh(expiresIn) {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer)
    }

    if (!expiresIn) {
      return
    }

    const delayMs = Math.max(expiresIn * REFRESH_AT_FRACTION * 1000, MIN_REFRESH_DELAY_MS)

    this._refreshTimer = setTimeout(() => {
      this._refreshTimer = null

      if (!userActivity.isRecentlyActive()) {
        authLogger.debug('token refresh: skipping proactive refresh, user inactive')
        return
      }

      authLogger.debug('token refresh: proactive refresh triggered')
      this.attemptRefresh().catch(() => {
        // Error already handled by attemptRefresh via onRefreshError
      })
    }, delayMs)
  }

  /**
   * Attempt to refresh the token. Concurrent calls share the same in-flight promise (mutex).
   * @returns {Promise<{expiresIn?: number}>}
   */
  attemptRefresh() {
    if (!this._refreshFn) {
      return Promise.reject(new Error('Token refresh service not configured'))
    }

    if (this._refreshPromise) {
      return this._refreshPromise
    }

    this._refreshPromise = this._refreshFn()
      .then((result) => {
        if (result?.expiresIn) {
          this.scheduleRefresh(result.expiresIn)
        }
        return result
      })
      .catch((error) => {
        if (this._onRefreshError) {
          this._onRefreshError(error)
        }
        throw error
      })
      .finally(() => {
        this._refreshPromise = null
      })

    return this._refreshPromise
  }

  /**
   * Clear scheduled refresh and reset state.
   */
  teardown() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer)
      this._refreshTimer = null
    }

    this._refreshFn = null
    this._onRefreshError = null
    this._refreshPromise = null
  }
}

export const tokenRefreshService = new TokenRefreshService()
